import { useState } from 'react';
import { useAuth } from '../app/auth';
import { api, describeError } from '../lib/api';
import { useAsync } from '../lib/useAsync';
import {
  Badge,
  Button,
  Card,
  CardHeader,
  Modal,
  Notice,
  PageHeader,
  Skeleton,
} from '../components/ui';

/**
 * 平台开关。
 *
 * 每个开关对应一个「立即生效但不需重新部署」的运营动作。
 * 关闭「平台登录」是破坏性操作（会把所有人踢下线），所以走二次确认。
 */

interface FlagView {
  key: string;
  label: string;
  description: string;
  group: 'access' | 'billing' | 'provider' | 'assistant';
  default: boolean;
  enabled: boolean;
  overridden: boolean;
  offImpact: string;
  destructive: boolean;
  updatedAt: string | null;
  updatedBy: string | null;
}

interface FlagEvent {
  id: string;
  at: string;
  actor: string;
  key: string;
  oldValue: string | null;
  newValue: string;
}

interface FlagsResponse {
  flags: FlagView[];
  events: FlagEvent[];
}

const GROUP_META: Record<FlagView['group'], { title: string; hint: string }> = {
  assistant: {
    title: 'AI 客服 · 全站微服务控制器',
    hint: '总开关控制两端；关闭任一端入口不会影响另一端。提示词与知识库在「AI 客服」页维护。',
  },
  access: {
    title: '访问控制',
    hint: '关闭后立即生效，不需要重新部署。',
  },
  billing: {
    title: '交易',
    hint: '控制用户是否可以在控制台下单购买服务。',
  },
  provider: {
    title: '支付通道',
    hint: '已配置的通道可以临时禁用，不影响其他通道。',
  },
};

/** 破坏性开关的二次确认文案。登录与 AI 客服的后果不同，不能共用一套说明。 */
function destructiveCopy(flag: FlagView): {
  title: string;
  description: string;
  button: string;
  body: string;
  note: string;
} {
  if (flag.key === 'assistant.enabled') {
    return {
      title: '确认关闭 AI 客服？',
      description: '关闭后官网与控制台的悬浮入口会立即消失。',
      button: '关闭 AI 客服',
      body: '关闭后所有用户都无法再使用 AI 客服，正在进行的对话会在下一条消息收到「服务已关闭」。',
      note: '提示词与知识库不会被删除，重新打开总开关即可恢复。',
    };
  }
  if (flag.key === 'login.enabled') {
    return {
      title: '确认关闭平台登录？',
      description: '这是一个破坏性操作，会让所有在线用户立即下线。',
      button: '关闭并强制下线',
      body: '关闭后所有用户会被立即登出，并且无法重新登录，直到你重新打开这个开关。',
      note: '这是一个破坏性操作：登录会话与 refresh token 会被全部吊销。请确认这是预期行为。',
    };
  }
  return {
    title: `确认关闭「${flag.label}」？`,
    description: '关闭后立即生效。',
    button: '确认关闭',
    body: flag.offImpact,
    note: '重新打开即可恢复。',
  };
}

function formatTime(value: string | null): string {
  if (value === null) return '—';
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleString('zh-CN', { hour12: false });
}

export function SettingsPage() {
  const { can } = useAuth();
  const canManage = can('settings.manage');

  const [version, setVersion] = useState(0);
  const [saving, setSaving] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [saved, setSaved] = useState<string | null>(null);
  const [pendingDestructive, setPendingDestructive] = useState<FlagView | null>(null);

  const state = useAsync<FlagsResponse>(() => api.get('/api/settings/flags'), [version]);
  const flags = state.data?.flags ?? [];

  async function applyChange(flag: FlagView, next: boolean) {
    setSaving(flag.key);
    setError(null);
    setSaved(null);
    try {
      await api.patch('/api/settings/flags', { flags: { [flag.key]: next } });
      setVersion((value) => value + 1);
      setSaved(`${flag.label} 已${next ? '开启' : '关闭'}，最多 30 秒内全站生效`);
    } catch (caught) {
      setError(describeError(caught));
    } finally {
      setSaving(null);
    }
  }

  function requestChange(flag: FlagView, next: boolean) {
    if (!canManage) return;
    if (!next && flag.destructive) {
      setPendingDestructive(flag);
      return;
    }
    void applyChange(flag, next);
  }

  const groups: FlagView['group'][] = ['assistant', 'access', 'billing', 'provider'];

  return (
    <div className="space-y-5">
      <PageHeader
        title="平台开关"
        description="统一管理影响全站用户行为的开关。修改后无需重新部署，下游服务会在 30 秒内同步。"
        action={
          <Button onClick={() => setVersion((value) => value + 1)} loading={state.loading}>
            刷新
          </Button>
        }
      />

      {!canManage ? (
        <Notice tone="info">
          当前角色只能查看开关。调整开关需要「调整平台开关」权限，请联系超级管理员或运营。
        </Notice>
      ) : null}

      {error ? <Notice tone="danger">{error}</Notice> : null}
      {saved ? <Notice tone="ok">{saved}</Notice> : null}
      {state.error ? <Notice tone="danger">{describeError(state.error)}</Notice> : null}

      {state.loading && state.data === null ? (
        <Skeleton className="h-40 w-full" />
      ) : (
        groups.map((group) => {
          const items = flags.filter((flag) => flag.group === group);
          if (items.length === 0) return null;
          const meta = GROUP_META[group];

          return (
            <Card key={group}>
              <CardHeader title={meta.title} hint={meta.hint} />
              <ul className="divide-y divide-line">
                {items.map((flag) => {
                  const busy = saving === flag.key;
                  return (
                    <li key={flag.key} className="flex items-start justify-between gap-4 py-3">
                      <div className="min-w-0">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-[13px] font-medium text-fg">{flag.label}</span>
                          <Badge tone={flag.enabled ? 'ok' : 'warn'}>
                            {flag.enabled ? '已开启' : '已关闭'}
                          </Badge>
                          {flag.overridden ? (
                            <Badge tone="brand">已手动调整</Badge>
                          ) : (
                            <Badge tone="neutral">默认值</Badge>
                          )}
                          {flag.destructive ? <Badge tone="danger">破坏性</Badge> : null}
                        </div>
                        <p className="mt-1 text-xs text-fg-muted">{flag.description}</p>
                        <p className="mt-1 text-xs text-fg-subtle">关闭后：{flag.offImpact}</p>
                        <p className="mt-1 font-mono text-[11px] text-fg-subtle">{flag.key}</p>
                        {flag.updatedAt ? (
                          <p className="mt-1 text-[11px] text-fg-subtle">
                            最近修改：{formatTime(flag.updatedAt)} · {flag.updatedBy}
                          </p>
                        ) : null}
                      </div>

                      <div className="shrink-0">
                        <label className="flex cursor-pointer items-center gap-2 text-[12px] text-fg-muted">
                          <input
                            type="checkbox"
                            className="h-4 w-4 accent-[#1366ec]"
                            checked={flag.enabled}
                            disabled={!canManage || busy}
                            onChange={(event) => requestChange(flag, event.target.checked)}
                          />
                          {busy ? '保存中…' : flag.enabled ? '开启' : '关闭'}
                        </label>
                      </div>
                    </li>
                  );
                })}
              </ul>
            </Card>
          );
        })
      )}

      <Card>
        <CardHeader title="最近变更" hint="最多保留最近 30 条，完整记录见审计页" />
        {(state.data?.events.length ?? 0) === 0 ? (
          <p className="py-6 text-center text-[13px] text-fg-muted">还没有改动记录。</p>
        ) : (
          <ul className="divide-y divide-line">
            {(state.data?.events ?? []).map((event) => (
              <li key={event.id} className="flex flex-wrap items-center justify-between gap-2 py-2 text-[12px]">
                <span className="font-mono text-fg-muted">{event.key}</span>
                <span className="text-fg">
                  {event.oldValue ?? 'default'} → {event.newValue}
                </span>
                <span className="text-fg-subtle">
                  {formatTime(event.at)} · {event.actor}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      <Modal
        open={pendingDestructive !== null}
        title={pendingDestructive ? destructiveCopy(pendingDestructive).title : ''}
        description={pendingDestructive ? destructiveCopy(pendingDestructive).description : undefined}
        onClose={() => setPendingDestructive(null)}
        footer={
          <>
            <Button onClick={() => setPendingDestructive(null)}>取消</Button>
            <Button
              variant="primary"
              loading={pendingDestructive !== null && saving === pendingDestructive.key}
              onClick={() => {
                const flag = pendingDestructive;
                if (flag === null) return;
                setPendingDestructive(null);
                void applyChange(flag, false);
              }}
            >
              {pendingDestructive ? destructiveCopy(pendingDestructive).button : '确认关闭'}
            </Button>
          </>
        }
      >
        <p className="text-[13px] text-fg">{pendingDestructive ? destructiveCopy(pendingDestructive).body : ''}</p>
        <p className="mt-2 text-[13px] text-fg-muted">
          {pendingDestructive ? destructiveCopy(pendingDestructive).note : ''}
        </p>
      </Modal>
    </div>
  );
}
